/**
 * Custom hook that owns sidebar navigation state for the Auth Letter
 * Upload modal — the currently selected authorization tab and the
 * set of tabs that are still disabled (locked until the user moves to them
 * via "Save & Next" or selects them explicitly).
 */
import { useCallback, useState } from "react";

/** Public surface returned by useAuthNavigation. */
export interface UseAuthNavigationReturn {
  selectedAuthorizationIndex: number;
  disabledIndexes: Set<number>;
  selectTab: (index: number) => void;
  deleteTab: (index: number, newLength: number) => number;
  addTab: (newIndex: number) => void;
  advanceToNextTab: (totalLength: number) => void;
}

/**
 * Manages selectedAuthorizationIndex and disabledIndexes for the sidebar.
 *
 * @returns Navigation state plus selectTab / deleteTab / addTab /
 *   advanceToNextTab helpers.
 */
export const useAuthNavigation = (): UseAuthNavigationReturn => {
  const [selectedAuthorizationIndex, setSelectedAuthorizationIndex] =
    useState<number>(0);
  const [disabledIndexes, setDisabledIndexes] = useState<Set<number>>(
    new Set(),
  );

  /** Selects the given tab without touching disabled state. */
  const selectTab = useCallback((index: number) => {
    setSelectedAuthorizationIndex(index);
  }, []);

  /**
   * Handles tab deletion bookkeeping: re-indexes disabledIndexes, picks the
   * next-selected tab, and makes sure the newly-selected tab is enabled.
   *
   * @param index - Index of the tab being removed.
   * @param newLength - Length of the data list *after* removal.
   * @returns The newly-selected tab index (for callers that need it).
   */
  const deleteTab = useCallback((index: number, newLength: number) => {
    const nextSelected =
      index >= newLength ? Math.max(newLength - 1, 0) : index;

    setSelectedAuthorizationIndex(nextSelected);
    setDisabledIndexes((prev) => {
      const shifted = new Set<number>();
      for (const di of prev) {
        if (di === index) continue;
        shifted.add(di > index ? di - 1 : di);
      }
      shifted.delete(nextSelected);
      return shifted;
    });

    return nextSelected;
  }, []);

  /** Activates a freshly-added tab; the new tab is implicitly enabled. */
  const addTab = useCallback((newIndex: number) => {
    setSelectedAuthorizationIndex(newIndex);
  }, []);

  /**
   * Moves to the next tab (used by Save & Next) and unlocks it.
   * If we are already on the final tab, this is a no-op.
   */
  const advanceToNextTab = useCallback((totalLength: number) => {
    setSelectedAuthorizationIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      if (nextIndex < totalLength) {
        setDisabledIndexes((prev) => {
          const next = new Set(prev);
          next.delete(nextIndex);
          return next;
        });
        return nextIndex;
      }
      return prevIndex;
    });
  }, []);

  return {
    selectedAuthorizationIndex,
    disabledIndexes,
    selectTab,
    deleteTab,
    addTab,
    advanceToNextTab,
  };
};
